import { useMutation } from '@tanstack/react-query'
import { Form as FormikForm, Formik, FormikProps } from 'formik'
import React, { useState } from 'react'
import {
  LuCheck,
  LuCircleCheck,
  LuCirclePlus,
  LuSquare,
  LuSquareCheck,
  LuTriangleAlert,
  LuX,
} from 'react-icons/lu'
import * as yup from 'yup'

import { EXPECTED_AGE_OF_DEATH } from '@/components/flows/CompassFlow/constants'
import DateOfBirthInput from '@/components/form/DateOfBirthInput'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { DATE_FORMATS } from '@/lib/constants'
import {
  convertDate,
  convertIsoDate,
  formatDate,
} from '@/lib/helpers/dateHelpers'
import { yupSchemas } from '@/lib/helpers/yupSchemas'
import clientService from '@/lib/services/clientService'
import { cn } from '@/lib/utils'
import {
  ReferralsImportItem,
  ReferralsImportItemPartial,
  ReferralsImportOnClientDuplicate,
} from '@/redux/slices/referralsImportBase'

interface Props {
  importItem: ReferralsImportItem
  updateImportItem: (item: ReferralsImportItemPartial) => void
}

const TELEPHONE_PREFIX = 'tel.: '
const DATE_OF_BIRTH_PREFIX = 'dob: '

interface DateOfBirthFormValues {
  dateOfBirth: string | null
}

const ClientCell: React.FC<Props> = ({ importItem, updateImportItem }) => {
  const [edit, setEdit] = useState<
    false | 'name' | 'email' | 'telephone' | 'dateOfBirth'
  >(false)
  const updateEmailMutation = useMutation({
    mutationFn: async (email: string) => {
      if (email == '' || !importItem.importingCompany) {
        return null
      }
      // find a client by email
      const clients = await clientService.searchClientsByEmail({
        email: email,
        companyId: importItem.importingCompany.id,
      })
      if (clients['hydra:totalItems'] > 0) {
        // assign existing client and email to import item
        const existingClient = clients['hydra:member'][0]!
        const existingClientImportData = {
          '@context': '',
          '@id': existingClient['@id'],
          '@type': existingClient['@type'],
          dateOfBirth: existingClient.dateOfBirth,
          email: existingClient.email,
          firstName: existingClient.firstName,
          id: existingClient.id,
          lastName: existingClient.lastName,
          telephone: existingClient.telephone,
        }
        updateImportItem({
          refUUID: importItem.refUUID,
          sourceClientEmail: email,
          importingCompanyClient: existingClientImportData,
        })
      } else {
        // update email in import item
        updateImportItem({
          refUUID: importItem.refUUID,
          sourceClientEmail: email,
        })
      }
      setEdit(false)
      return null
    },
  })
  const formNameRef = React.createRef<HTMLFormElement>()
  const formEmailRef = React.createRef<HTMLFormElement>()
  const formTelephoneRef = React.createRef<HTMLFormElement>()
  const formikDateOfBirthRef =
    React.useRef<FormikProps<DateOfBirthFormValues>>(null)

  const handleNameSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    updateImportItem({
      refUUID: importItem.refUUID,
      sourceClientFirstName: event.currentTarget.firstName.value,
      sourceClientLastName: event.currentTarget.lastName.value,
    })
    setEdit(false)
  }

  const handleEmailSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    updateEmailMutation.mutate(event.currentTarget.email.value)
  }
  const handleEmailInputChange = () => {
    formEmailRef.current?.requestSubmit()
  }

  const handleTelephoneSubmit = async (
    event: React.FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault()
    updateImportItem({
      refUUID: importItem.refUUID,
      sourceClientTelephone: event.currentTarget.telephone.value,
    })
    setEdit(false)
  }
  const handleTelephoneInputChange = () => {
    formTelephoneRef.current?.requestSubmit()
  }

  const handleDateOfBirthSubmit = async (values: DateOfBirthFormValues) => {
    updateImportItem({
      refUUID: importItem.refUUID,
      clientDateOfBirth: values.dateOfBirth
        ? convertDate(
            values.dateOfBirth,
            DATE_FORMATS.DAY_MONTH_YEAR,
            DATE_FORMATS.YEAR_MONTH_DAY,
          )
        : null,
    })
    setEdit(false)
  }

  const handleOnClientDuplicateChange = (
    value: ReferralsImportOnClientDuplicate,
  ) => {
    updateImportItem({ refUUID: importItem.refUUID, onClientDuplicate: value })
  }

  const handlePauseEmailsToggle = () => {
    if (importItem.canPauseClientEmails) {
      updateImportItem({
        refUUID: importItem.refUUID,
        hasPausedClientEmails: !importItem.hasPausedClientEmails,
      })
    }
  }

  const isFirstNameDiff: boolean =
    !!importItem.importingCompanyClient &&
    !!importItem.sourceClientFirstName &&
    importItem.importingCompanyClient.firstName !==
      importItem.sourceClientFirstName

  const isLastNameDiff: boolean =
    !!importItem.importingCompanyClient &&
    !!importItem.sourceClientLastName &&
    importItem.importingCompanyClient.lastName !==
      importItem.sourceClientLastName

  const isTelephoneDiff: boolean =
    !!importItem.importingCompanyClient &&
    !!importItem.sourceClientTelephone &&
    importItem.importingCompanyClient.telephone !==
      importItem.sourceClientTelephone

  const isDateOfBirthDiff: boolean =
    !!importItem.importingCompanyClient &&
    !!importItem.clientDateOfBirth &&
    importItem.importingCompanyClient.dateOfBirth !==
      importItem.clientDateOfBirth

  const isDiff: boolean =
    isFirstNameDiff || isLastNameDiff || isTelephoneDiff || isDateOfBirthDiff

  const isUpdate = importItem.onClientDuplicate == 'update'

  const emailNotificationToggle = importItem.canPauseClientEmails ? (
    <Field orientation="horizontal">
      <Checkbox
        id="email-notifications"
        checked={!importItem.hasPausedClientEmails}
        onCheckedChange={handlePauseEmailsToggle}
        className="text-body-base inline-block"
      />
      <FieldLabel htmlFor="email-notifications" className="text-dim text-xs">
        Email notifications
      </FieldLabel>
    </Field>
  ) : (
    <br />
  )

  const view =
    edit != false ? edit : importItem.importingCompanyClient ? 'view' : 'add'

  return (
    <>
      {view === 'name' && (
        <>
          <form ref={formNameRef} onSubmit={handleNameSubmit}>
            <Field className="mb-4 gap-1">
              <FieldLabel htmlFor="firstName" className="text-xs">
                First name
              </FieldLabel>

              <Input
                placeholder="Jane"
                defaultValue={importItem.sourceClientFirstName}
                name="firstName"
                type="text"
              />
            </Field>

            <Field>
              <Label htmlFor="lastName" className="text-xs">
                Last name
              </Label>

              <Input
                placeholder="Jane"
                defaultValue={importItem.sourceClientLastName}
                name="lastName"
                type="text"
              />
            </Field>

            <span className="mt-1 block text-right">
              <Button type="submit" size="icon-xs" variant="outline-primary">
                <LuCheck />
              </Button>
            </span>
          </form>
        </>
      )}

      {view === 'email' && (
        <>
          <form ref={formEmailRef} onSubmit={handleEmailSubmit}>
            <Field className="gap-1">
              <FieldLabel htmlFor="email" className="text-xs">
                Email address
              </FieldLabel>

              <Input
                placeholder="john.smith@company.com"
                defaultValue={importItem.sourceClientEmail}
                name="email"
                type="email"
                required={true}
                onBlur={handleEmailInputChange}
              />
            </Field>

            <span className="mt-1 block text-right">
              <Button type="submit" size="icon-xs" variant="outline-primary">
                {updateEmailMutation.isPending ? <Spinner /> : <LuCheck />}
              </Button>
            </span>
          </form>
        </>
      )}

      {view === 'telephone' && (
        <>
          <form ref={formTelephoneRef} onSubmit={handleTelephoneSubmit}>
            <Field className="gap-1">
              <FieldLabel htmlFor="telephone" className="text-xs">
                Telephone
              </FieldLabel>

              <Input
                placeholder="07771231212"
                defaultValue={importItem.sourceClientTelephone}
                name="telephone"
                type="text"
                onBlur={handleTelephoneInputChange}
              />
            </Field>

            <span className="mt-1 block text-right">
              <Button type="submit" size="icon-xs" variant="outline-primary">
                <LuCheck />
              </Button>
            </span>
          </form>
        </>
      )}

      {view === 'dateOfBirth' && (
        <>
          <Formik
            innerRef={formikDateOfBirthRef}
            initialValues={{
              dateOfBirth: importItem.clientDateOfBirth
                ? convertIsoDate(
                    importItem.clientDateOfBirth,
                    DATE_FORMATS.DAY_MONTH_YEAR,
                  )
                : null,
            }}
            validationSchema={yup.object({
              dateOfBirth: yupSchemas.dayMonthYear().nullable(),
            })}
            onSubmit={handleDateOfBirthSubmit}
          >
            <FormikForm>
              <Field className="gap-1">
                <FieldLabel htmlFor="dateOfBirth" className="text-xs">
                  Date of birth
                </FieldLabel>

                <DateOfBirthInput
                  name="dateOfBirth"
                  maxAge={EXPECTED_AGE_OF_DEATH}
                />
              </Field>

              <span className="mt-1 block text-right">
                <Button
                  type="button"
                  size="icon-xs"
                  variant="outline-primary"
                  onClick={() => handleDateOfBirthSubmit({ dateOfBirth: null })}
                >
                  <LuX />
                </Button>

                <Button type="submit" size="icon-xs" variant="outline-primary">
                  <LuCheck />
                </Button>
              </span>
            </FormikForm>
          </Formik>
        </>
      )}

      {view === 'view' && importItem.importingCompanyClient && (
        <>
          <LuCircleCheck
            className={cn(
              'mr-1 inline-block -translate-y-px',
              isDiff && isUpdate ? 'text-info' : 'text-success',
            )}
          />

          <DiffValue
            isDiff={isFirstNameDiff && isUpdate}
            oldValue={importItem.importingCompanyClient.firstName}
            newValue={importItem.sourceClientFirstName}
          />
          {', '}
          <DiffValue
            isDiff={isLastNameDiff && isUpdate}
            oldValue={importItem.importingCompanyClient.lastName}
            newValue={importItem.sourceClientLastName}
          />
          {emailNotificationToggle}

          <span className="text-dim text-xs">
            {importItem.importingCompanyClient.email}
          </span>

          {(importItem.importingCompanyClient.telephone ||
            importItem.sourceClientTelephone) && (
            <>
              <br />
              <span className="text-dim text-xs">
                {TELEPHONE_PREFIX}
                <DiffValue
                  isDiff={isTelephoneDiff && isUpdate}
                  oldValue={
                    importItem.importingCompanyClient.telephone || 'undefined'
                  }
                  newValue={importItem.sourceClientTelephone || 'undefined'}
                />
              </span>
            </>
          )}

          {(importItem.importingCompanyClient.dateOfBirth ||
            importItem.clientDateOfBirth) && (
            <>
              <br />
              <span className="text-dim text-xs">
                {DATE_OF_BIRTH_PREFIX}
                <DiffValue
                  isDiff={isDateOfBirthDiff && isUpdate}
                  oldValue={
                    importItem.importingCompanyClient.dateOfBirth
                      ? formatDate(
                          importItem.importingCompanyClient.dateOfBirth,
                        )
                      : 'undefined'
                  }
                  newValue={
                    importItem.clientDateOfBirth
                      ? formatDate(importItem.clientDateOfBirth)
                      : 'undefined'
                  }
                />
              </span>
            </>
          )}

          {isDiff && (
            <span className="text-dim show-on-row-hover block text-xs">
              <Button
                variant="plain"
                className="mr-1 gap-0.5"
                onClick={() =>
                  handleOnClientDuplicateChange(
                    importItem.onClientDuplicate == 'keep-original'
                      ? 'update'
                      : 'keep-original',
                  )
                }
              >
                {isUpdate ? (
                  <LuSquareCheck className="size-3 translate-y-px" />
                ) : (
                  <LuSquare className="size-3 translate-y-0.5" />
                )}
                update
              </Button>

              <span className="">
                {importItem.onClientDuplicate == 'keep-original'
                  ? '(keep existing)'
                  : ''}
              </span>
            </span>
          )}
        </>
      )}

      {view === 'add' && (
        <>
          <LuCirclePlus className="text-info mr-1 inline-block -translate-y-0.5" />

          <span onClick={() => setEdit('name')}>
            {importItem.sourceClientFirstName}
            {', '}
            {importItem.sourceClientLastName}
          </span>
          <span className="show-on-row-hover text-xs">
            {' '}
            <Button variant="plain" onClick={() => setEdit('name')}>
              edit
            </Button>
          </span>

          {emailNotificationToggle}

          <span className="text-dim text-xs" onClick={() => setEdit('email')}>
            {!importItem.sourceClientEmailValid && (
              <LuTriangleAlert className="text-destructive mr-1 inline-block" />
            )}

            {importItem.sourceClientEmail}
            {importItem.autogeneratedClientEmail ==
              importItem.sourceClientEmail && (
              <span className="text-warning"> (generated)</span>
            )}

            {!importItem.sourceClientEmailValid && (
              <span className="text-destructive"> (invalid)</span>
            )}
          </span>
          <span className="show-on-row-hover text-xs">
            {' '}
            <Button variant="plain" onClick={() => setEdit('email')}>
              edit
            </Button>
          </span>

          {importItem.sourceClientTelephone && (
            <>
              <br />
              <span
                className="text-dim text-xs"
                onClick={() => setEdit('telephone')}
              >
                {TELEPHONE_PREFIX}
                {importItem.sourceClientTelephone}
              </span>
              <span className="show-on-row-hover text-xs">
                {' '}
                <Button variant="plain" onClick={() => setEdit('telephone')}>
                  edit
                </Button>
              </span>
            </>
          )}

          {importItem.clientDateOfBirth && (
            <>
              <br />
              <span className="text-dim text-xs">
                {DATE_OF_BIRTH_PREFIX}
                {formatDate(importItem.clientDateOfBirth)}
              </span>
              <span className="show-on-row-hover text-xs">
                {' '}
                <Button variant="plain" onClick={() => setEdit('dateOfBirth')}>
                  edit
                </Button>
              </span>
            </>
          )}

          {!importItem.sourceClientTelephone && (
            <>
              <span className="show-on-row-hover text-xs">
                <br />
                <Button variant="plain" onClick={() => setEdit('telephone')}>
                  add telephone
                </Button>
              </span>
            </>
          )}

          {!importItem.clientDateOfBirth && (
            <>
              <span className="show-on-row-hover text-xs">
                <br />
                <Button variant="plain" onClick={() => setEdit('dateOfBirth')}>
                  add date of birth
                </Button>
              </span>
            </>
          )}
        </>
      )}
    </>
  )
}

function DiffValue({
  isDiff,
  oldValue,
  newValue,
}: {
  isDiff: boolean
  oldValue: string
  newValue: string
}) {
  return (
    <>
      {isDiff ? (
        <>
          <span className="line-through opacity-75">{oldValue}</span> {newValue}
        </>
      ) : (
        oldValue
      )}
    </>
  )
}

export default ClientCell
